(ns app.domain.property.model
  (:require #?(:clj  [app.time :as time]
               :cljs [core.front.date-util :as date-util])
            [app.domain.group.model :as group-model]
            [app.domain.municipality.model :as muni-model]
            [app.domain.util :as du]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.child-entity :as child]
            [potpuri.core :as p]))

(def property-states
  #{:property.state/draft
    :property.state/applicant-draft
    :property.state/unverified
    :property.state/active
    :property.state/historical})

(def default-search-states
  #{:property.state/active})

(def property-creation-keys-for-user-entered [:property/id
                                              :property/legal
                                              :property/type
                                              :property/integration-id])

(def property-creation-keys (concat property-creation-keys-for-user-entered
                                    [:property/secondary-id
                                     :property/type-code
                                     :property/ward
                                     :property/description
                                     :property/zoning
                                     :property/from-st
                                     :property/to-st]))

(def property-gis-import-session-max-age-days 7)

(def ^:private special-property-key-requirements
  "Because of MPAC, B permits in CA-ON require legal description"
  {[:CA-ON :permit-type/B] #{:property/legal}})

(defn required-property-keys [subdivision permit-type]
  (set/union (get special-property-key-requirements [subdivision permit-type])
             #{:property/id}))

(defn optional-manual-property-id? [domain-group]
  (group-model/has-feature? domain-group :group.feature/optional-manual-property-id))

(defn required-property-input-keys [subdivision permit-type domain-group]
  (cond-> (required-property-keys subdivision permit-type)
    (optional-manual-property-id? domain-group) (disj :property/id)))

(defn geojson-feature
  ([geometry]
   (geojson-feature geometry nil nil))
  ([geometry id properties]
   (p/assoc-if
     {:type     "Feature"
      :geometry geometry}
     :id id
     :properties properties)))

(defn geojson-feature-collection [features]
  {:type     "FeatureCollection"
   :features features})

(defn property-type-text
  ([tr {:property/keys [type-code municipality]}]
   (when type-code
     (property-type-text tr type-code (:municipality/country municipality))))
  ([tr type-code country]
   (when type-code
     (let [translation (tr [(keyword (str country "-property-code") type-code)
                            (keyword "property-code" type-code)
                            ""])]
       (str type-code (when-not (str/blank? translation)
                        (str " - " translation)))))))


(defn gis-property->wizard-property
  "Turn property search result into the same format as used by the create app
  wizard, from which we borrow our display widget."
  [gis-data]
  (-> (merge gis-data
             (:property gis-data))
      (dissoc :property)))

(defn additional-addresses
  [property]
  (remove #(= (:property/primary-address property) %) (:property/addresses property)))

(defn full-address
  [property]
  (:address/full-address (:property/primary-address property)))


(defn municipality-name [lang {:property/keys [municipality]}]
  (muni-model/municipality-name municipality lang))


(defn property-has-valid-id?
  "Properties that only have zeros or dots in them are used as 'placeholder roll numbers' in some municipalities"
  [{:property/keys [id]}]
  (and (not (str/blank? id))
       (not-every? #(contains? #{\. \0 \space} %) id)))


(defn gis-verified? [{:property/keys [data-source]}]
  (= data-source :property.data-source/gis))


(defn property-tr-key [domain-group k]
  (-> domain-group
      group-model/get-i18n-mapping
      (get k k)))

(defn property-tr-fn
  "Provides a translation function for property keys as these may be configured per domain group or superdomain group.
   `tr` must be the front- or back-end `tr` function. Second argument must be a map or entity containing
    :group/property-i18n-mapping, e.g. a domain group or superdomain group."
  [tr domain-group-like]
  (-> domain-group-like
      group-model/get-i18n-mapping
      (i18n-model/wrap-tr-fn tr)))

(defn format-property-id-for-sentence [property-tr]
  (p/condas-> (property-tr [:property/id]) text
    ;; Leave short acronyms as-is, lower case e.g. "roll number"
    (>= (count text) 4) (str/lower-case text)))

(defn property-attrs-str [property]
  (->> property
       :property/legacy-attributes
       (map :legacy-property-attribute/value)
       (str/join ", ")))

(def draft?
  (du/in-state-fn :property.state/applicant-draft :property.state/draft))

(def attribute-visibilities
  [:property-attribute-template.visibility/read-authority
   :property-attribute-template.visibility/write-authority
   :property-attribute-template.visibility/public])

(def attribute-template-types
  [:property-attribute-template.type/select
   :property-attribute-template.type/integer
   :property-attribute-template.type/decimal
   :property-attribute-template.type/text
   :property-attribute-template.type/toggle
   :property-attribute-template.type/date
   :property-attribute-template.type/date-range])

(defn valid-search-attribute-value?
  "True when `value` matches the given Property search attribute template type. A date-range search
   value is a single date."
  [template-type value]
  (case template-type
    (:property-attribute-template.type/select
      :property-attribute-template.type/text)       (string? value)
    :property-attribute-template.type/integer       (integer? value)
    :property-attribute-template.type/decimal       (number? value)
    :property-attribute-template.type/toggle        (boolean? value)
    (:property-attribute-template.type/date
      :property-attribute-template.type/date-range) (inst? value)
    false))

(def compatible-template-types
  "Type may only be changed between select and text, because other type changes would require a migration of
   every existing attribute instance."
  {:property-attribute-template.type/select [:property-attribute-template.type/text]
   :property-attribute-template.type/text   [:property-attribute-template.type/select]})

(def ^{:arglists '([property-attribute-template lang])} attribute-template-name
  "Property attribute (template) name"
  (i18n-model/translation-fallback-fn :property-attribute-template/names))

(defn toggle-attribute-true-label
  "Label text for toggle attribute when set to true"
  [property-attribute-template lang tr]
  (if-let [labels (:property-attribute-template/toggle-true-labels property-attribute-template)]
    (i18n-model/get-text-from-entities-or-map labels lang)
    (tr [:property-attribute-template-toggle-default/true])))

(defn toggle-attribute-false-label
  "Label text for toggle attribute when set to true"
  [property-attribute-template lang tr]
  (if-let [labels (:property-attribute-template/toggle-false-labels property-attribute-template)]
    (i18n-model/get-text-from-entities-or-map labels lang)
    (tr [:property-attribute-template-toggle-default/false])))

(defn- date-in-tz [date tz]
  #?(:clj  (time/in-zone date tz)
     :cljs (date-util/in-tz date tz)))

(defn- format-date-in-tz [property-attribute format-date date]
  (when (inst? date)
    (let [tz (-> property-attribute
                 :property-attribute/template
                 :property-attribute-template/group
                 :group/tz)]
      (cond-> date
        tz (date-in-tz tz)
        :always format-date))))

(defn toggle-attribute-value [property-attribute-template property-attribute lang tr]
  (if (:property-attribute/value-boolean property-attribute)
    (toggle-attribute-true-label property-attribute-template
                                 lang
                                 tr)
    (toggle-attribute-false-label property-attribute-template
                                  lang
                                  tr)))

(defn attribute-value
  "Formats current property attribute value to string. If the attribute is a date, it is formatted in the
   property group's time zone.
   `format-date` should be a function that formats a js/Date or ZonedDateTime to localized string.

   Note that for the 4-arity version `property-attribute` must be an entity (or a map with the
   `:property-attribute/template` key). For the 5-arity version `property-attribute-template` is provided separately,
   and can be an entity or a map with enough keys to access the template type and toggle labels."
  ([property-attribute lang tr format-date]
   (attribute-value (:property-attribute/template property-attribute) property-attribute lang tr format-date))
  ([property-attribute-template property-attribute lang tr format-date]
   (case (:property-attribute-template/type property-attribute-template)
     (:property-attribute-template.type/text
       :property-attribute-template.type/select) (-> property-attribute
                                                     :property-attribute/value-localized
                                                     (i18n-model/get-text lang))
     :property-attribute-template.type/integer (-> property-attribute :property-attribute/value-long str)
     :property-attribute-template.type/decimal (-> property-attribute :property-attribute/value-decimal str)
     :property-attribute-template.type/toggle (toggle-attribute-value property-attribute-template property-attribute lang tr)
     :property-attribute-template.type/date (->> property-attribute
                                                 :property-attribute/value-date
                                                 (format-date-in-tz property-attribute format-date))
     :property-attribute-template.type/date-range (->> [:property-attribute/value-start-date
                                                        :property-attribute/value-end-date]
                                                       (map (fn [k]
                                                              (->> (k property-attribute)
                                                                   (format-date-in-tz property-attribute format-date))))
                                                       (str/join " - "))
     nil)))

(defn enabled-property-attribute-templates-in-domain-group
  "Get enabled property attribute templates that belong to domain-group."
  [domain-group]
  (->> domain-group
       :property-attribute-template/_group
       ; TODO: Following property template types cannot be imported at the moment
       (remove #(contains? #{:property-attribute-template.type/date-range}
                           (:property-attribute-template/type %)))
       (filter (du/in-state-fn :property-attribute-template.state/enabled))))

(defn associated-entities [property]
  (concat (:workspace/_properties property)
          (:workspace/_default-neighbour-properties property)
          (:workspace/_neighbour-properties property)
          (some-> property :complaints/_property vector)
          (:licence/_properties property)
          (:business/_properties property)))

(def ^{:arglists '([import-session])} import-session-tasks
  (child/child-entities-of-type-fn :entity.type/async-job))

(def ^{:arglists '([import-session])} import-session-duplicate-resolutions
  (child/child-entities-of-type-fn :entity.type/property-import-duplicate-resolution))

(defn duplicate-resolutions-by-property-id [resolutions]
  (->> resolutions
       (map (fn [{:property-import-duplicate-resolution/keys [property-id main additional]}]
              [property-id (p/assoc-if {:main main} :additional (not-empty (set additional)))]))
       (into {})))

(defn- gis-fetch-task? [task]
  (= :async-job.task/property-gis-fetch (:async-job/task task)))

(defn duplicate-records-exportable?
  "True when an import session's GIS fetch has finished and no import has started, so its temp records are a
   complete snapshot. `tasks` are the session's async jobs."
  [tasks]
  (let [fetch-tasks (filter gis-fetch-task? tasks)]
    (and (some (du/in-state-fn :async-job.state/ready) fetch-tasks)
         (not-any? (du/in-state-fn :async-job.state/running) fetch-tasks)
         (not-any? #(= :async-job.task/property-gis-import (:async-job/task %)) tasks))))

(defn user-is-property-manager?
  "Note! This function is for front end, it does not work for plain user
  entity, as it relies on :user/administered-groups and :user/domain-groups
  being populated (see `app.domain.user.core/export-authority-data`)."
  [user]
  (or (->> user
           :user/administered-groups
           (mapcat :group-roles)
           (some #(= % :group.role/property-management)))
      (->> user
           :user/domain-groups
           (mapcat :group-roles)
           (some #(= % :group.role/property-management)))))

(def ^{:arglists '([property])} property-attachments
  (child/child-entities-of-type-fn :entity.type/attachment))

(defn validate-select-option [{:property-attribute-template/keys [options]} value-localized]
  (->> options
       (map (comp i18n-model/strings-map :property-attribute-template-option/values))
       (some #(= % value-localized))))

(def ^{:arglists '([property-attribute-template-option lang])} attribute-template-option-name
  "Property attribute template option name"
  (i18n-model/translation-fallback-fn :property-attribute-template-option/values))

(def mass-deletion-limit 50)

(defn deletion-count-over-limit? [property-count]
  (> property-count mass-deletion-limit))

(defmethod du/is-active? :entity.type/property-unit
  [{:domain/keys [state]}]
  (= state :property-unit.state/active))

(defmethod i18n-model/get-localized-string-attrs :entity.type/property-unit-designator
  [_]
  [:property-unit-designator/names])

(def ^{:arglists '([property-unit-designator lang])} unit-designator-name
  "Property unit designator name"
  (i18n-model/translation-fallback-fn :property-unit-designator/names))

(defn unit-display-key
  "Compute display-key from designator name and identifier, e.g. \"Apt 101\"."
  [designator-name identifier]
  (if (not-empty identifier)
    (str designator-name " " identifier)
    designator-name))

(defn localized-unit-display-key
  "Compute a unit's display-key for the render language via `designator-name-fn`."
  [unit designator-name-fn]
  (unit-display-key (designator-name-fn (:property-unit/designator unit))
                    (:property-unit/identifier unit)))

(defn active-property-units [property]
  (->> property :property-unit/_property (filter du/is-active?)))

(def active-or-unverified-property-unit-states
  #{:property-unit.state/active
    :property-unit.state/unverified})

(defn active-or-unverified-property-units [property]
  (->> property
       :property-unit/_property
       (filter (comp active-or-unverified-property-unit-states :domain/state))))

(defn address-unit-part
  "Return the unit suffix for an address:
     - 0 units         -> nil
     - 1 active unit   -> localized unit display-key
     - 2+ active units -> multi-unit-label"
  [units designator-name-fn multi-unit-label]
  (case (count units)
    0 nil
    1 (localized-unit-display-key (first units) designator-name-fn)
    multi-unit-label))

(defn format-address-with-units
  "Append a unit-info suffix to `address`:
     - 0 units         -> address unchanged
     - 1 active unit   -> \"<address>, <localized unit display-key>\"
     - 2+ active units -> \"<address>, <multi-unit-label>\"
   The 2-arity version takes a pre-computed unit-part string.
   The 4-arity version computes the unit-part from units,
   designator-name-fn and multi-unit-label."
  ([address unit-part]
   (cond-> address
     (and (not (str/blank? address))
          (not (str/blank? unit-part)))
     (str ", " unit-part)))
  ([address units designator-name-fn multi-unit-label]
   (format-address-with-units
     address (address-unit-part units designator-name-fn multi-unit-label))))

(defn address-with-units-for-property
  "Property address with its active unit info."
  [property designator-name-fn multi-unit-label]
  (format-address-with-units (full-address property)
                             (active-property-units property)
                             designator-name-fn
                             multi-unit-label))
